import { Box, Button, DatePickerBase, DatePickerBaseProps, IconButton, Tooltip } from "@compass/core";
import { CustomFilter, ListFilterBase } from "generated/openApi";
import PlaylistAddCheckCircleRoundedIcon from '@mui/icons-material/PlaylistAddCheckCircleRounded';
import PlaylistAddCircleIcon from "@mui/icons-material/PlaylistAddCircle";
import ClearIcon from "@mui/icons-material/Clear";
import { useEffect, useState } from "react";

interface FilterDrawerButtonProps<T> {
    listFilter: T;
    initListFilter: T;
    setFilterOpen: (shouldOpen: boolean) => void;
    clearFilter: (initFilter: T) => void;
    clearToBlank?: boolean;
}

export const FilterDrawerButton = <T extends ListFilterBase>(props: FilterDrawerButtonProps<T>) => {
    const [hasFilterValues, setHasFilterValues] = useState(true);

    useEffect(() => {
        if (isFilterModified(props.listFilter)) {
            setHasFilterValues(true);
        }
        else setHasFilterValues(false);
      }, [props.listFilter]);

    const isFilterModified = (filterToCheck: T) => {
        if (!props.initListFilter) return false;
        if (props.clearToBlank) {
            return hasMeaningfulFilterValues(normalizeFilter(filterToCheck));
        }

        return JSON.stringify(normalizeFilter(filterToCheck)) !== JSON.stringify(normalizeFilter(props.initListFilter));
    };

    const normalizeFilter = (filter: T) => ({
        ...filter,
        page: undefined,
        pageSize: undefined,
        sortObjects: undefined,
        searchString: undefined,
    });

    const clearAppliedFilters = () => {
        setHasFilterValues(false);
        props.clearFilter(props.clearToBlank ? getBlankFilter() : props.initListFilter);
    };

    const getBlankFilter = () => {
        return {
            page: props.listFilter.page,
            pageSize: props.listFilter.pageSize,
            sortObjects: props.listFilter.sortObjects,
            searchString: props.listFilter.searchString,
        } as T;
    };

    const hasMeaningfulFilterValues = (value: any): boolean => {
        if (value === null || value === undefined || value === "") return false;

        if (Array.isArray(value)) {
            return value.length > 0;
        }

        if (typeof value === "boolean") {
            return value;
        }

        if (typeof value === "object") {
            return Object.entries(value).some(([key, nestedValue]) => {
                if (["page", "pageSize", "sortObjects", "searchString"].includes(key)) return false;

                return hasMeaningfulFilterValues(nestedValue);
            });
        }

        return true;
    };

    return (
        <Tooltip title="Filters">
            <Box sx={{ position: "relative", display: "inline-flex" }}>
                <Button
                    variant="text"
                    onClick={() => props.setFilterOpen(true)}
                    sx={{ minWidth: 40, width: 40, height: 40, p: 0 }}
                >
                    {hasFilterValues && <PlaylistAddCheckCircleRoundedIcon color="success" sx={{ fontSize: "28px" }} />}
                    {!hasFilterValues && <PlaylistAddCircleIcon color="disabled" sx={{ fontSize: "28px" }} />}
                </Button>
                {hasFilterValues && (
                    <IconButton
                        onClick={(e) => {
                            e.stopPropagation();
                            clearAppliedFilters();
                        }}
                        size="small"
                        title="Clear filters"
                        sx={{
                            position: "absolute",
                            top: -4,
                            right: -4,
                            padding: "2px",
                            backgroundColor: "grey.300",
                            "&:hover": {
                                backgroundColor: "grey.400",
                            },
                            minWidth: "18px",
                            width: "18px",
                            height: "18px",
                        }}
                    >
                        <ClearIcon sx={{ fontSize: "12px" }} />
                    </IconButton>
                )}
            </Box>
        </Tooltip>
    );
};
